import * as React from "react";
import { SingleNavMenuLink, toLinkButton, toLinkDivider, toLinkPage, toLinkSpace } from "../identity/useNavMenu";
import { renderEventWarningIcon } from "../identity/navMenuIcons";
import { formSet, useCpFormAnyValueId, useCpFormModifiedAnyId } from "../gvc/testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { useAppDispatch } from "../../store/hooks";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../envVariables";
import { ContentLayout } from "../../components/layout/contentLayout";
import { SingleNavMenu } from "../../components/antd/layout/singleNavMenu";
import { CpTagsNew } from "../../components/form/CpTagsNew";
import { useEventlogs } from "../../reactHooks/useEventlog";
import { linksOf } from "../../services/cpln";
import { formActions } from "../../store/formSlice";
import { Events } from "../../components/detail/events";
import { AccessReportPage } from "../../components/detail/accessReport";
import { AuditTrail } from "../../components/detail/auditTrail";
import { Summary } from "./summary";
import { Org } from "../../schema/types/org/entity";
import { OrgDetailHeader } from "./header";
import { BillingAccountSummary } from "../../schema/types/account";
import { SessionTimeout } from "./sessionTimeout";
import { AuthConfig } from "./authConfig";
import { ExternalLogsTabs } from "./logs/externalLogsTabs";
import { Tracing } from "./tracing";
import { Observability } from "./observability";
import { ThreatDetection } from "./threatDetection";
import { CpRootError } from "../gvc/testNewForm/CpRootError";
import { TagColumnsGuide } from "./tagColumnsGuide";

interface Props {
  org: Org;
  account: BillingAccountSummary | undefined;
  isLoadingAccount: boolean;
  refetch?: () => void;
  updateAccount: () => Promise<void>;
}
export const OrgDetailContent: React.FC<Props> = ({ org, account, isLoadingAccount, updateAccount, refetch }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/*`);
  const { pathname } = useLocation();

  // eventlog
  const eventlogHook = useEventlogs(linksOf(org).self!, refetch);
  React.useEffect(() => {
    eventlogHook.setItemLastModified(org.lastModified);
  }, [org.lastModified]);
  React.useEffect(() => {
    if (pathname.includes("/events")) {
      return;
    }

    const id = setInterval(() => eventlogHook.fetchEventlogs(true), 30 * 1000);

    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [pathname]);

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValueId(formId, `meta.submitting`);
  const anyModified = useCpFormModifiedAnyId(formId);

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("Summary", "/summary", { names: ["description"], showDirty: true }),
      toLinkPage("Session Timeout", "/session-timeout", { names: ["sessionTimeoutSeconds"], showDirty: true }),
      toLinkPage("Auth Config", "/auth-config", { names: ["authConfig"], showDirty: true }),
      toLinkPage("External Logs", "/external-logs", { names: ["logging"], showDirty: true }),
      toLinkPage("Tracing", "/tracing", { names: ["tracing"], showDirty: true }),
      toLinkPage("Observability", "/observability", { names: ["observability"], showDirty: true }),
      toLinkPage("Threat Detection", "/threat-detection", { names: ["security"], showDirty: true }),
      toLinkPage("Tags", "/tags", { names: ["tags"], showDirty: true }),
      toLinkDivider(),
      toLinkPage("Events", "/events", { renderIcon: renderEventWarningIcon(eventlogHook.hasWarning) }),
      toLinkPage("Audit Trail", "/audit-trail"),
      toLinkPage("Access Report", "/access-report"),
    );

    if (anyModified) {
      links.push(
        toLinkSpace(),
        toLinkButton("Reset", {
          onClick: () => {
            dispatch(formActions.reset(formId));
            setTimeout(() => {
              dispatch(formActions.reset(formId));
            }, 0);
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Preview", {
          onClick: () => {
            formSet(formId, `meta.previewing`, true);
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Update", {
          onClick: async () => {
            await submit();
          },
          type: "primary",
          className: "action",
          disabled: submitting,
          loading: submitting,
        }),
      );
    }

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      if (!anyModified) {
        links.push(toLinkSpace());
      }
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
          disabled: submitting,
        }),
      );
    }

    return links;
  }, [anyModified, submitting, eventlogHook.hasWarning]);

  return (
    <BasePathContext.Provider value={basePath}>
      <OrgDetailHeader org={org} />
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={true} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route
                path={`summary/*`}
                element={
                  <Summary
                    org={org}
                    account={account}
                    isLoadingAccount={isLoadingAccount}
                    updateAccount={updateAccount}
                  />
                }
              />
              <Route path={`session-timeout/*`} element={<SessionTimeout />} />
              <Route path={`auth-config/*`} element={<AuthConfig />} />
              <Route path={`external-logs/*`} element={<ExternalLogsTabs />} />
              <Route path={`tracing/*`} element={<Tracing />} />
              <Route path={`observability/*`} element={<Observability />} />
              <Route path={`threat-detection/*`} element={<ThreatDetection />} />
              <Route
                path={`tags/*`}
                element={
                  <>
                    <TagColumnsGuide />
                    <CpTagsNew />
                  </>
                }
              />
              <Route
                path={`events`}
                element={
                  <Events
                    kind={"org"}
                    eventlogs={eventlogHook.eventlogs}
                    isLoading={eventlogHook.isLoading}
                    fetchEventlogs={eventlogHook.fetchEventlogs}
                    isFetching={eventlogHook.isFetching}
                    nextLink={eventlogHook.nextLink}
                  />
                }
              />
              <Route
                path={`audit-trail`}
                element={<AuditTrail kind={"org"} itemName={org.name} resourceId={org.id} version={org.version} selfLink={linksOf(org).self!} refetch={refetch} />}
              />
              <Route path={`access-report`} element={<AccessReportPage itemLink={linksOf(org).self!} />} />
              <Route path={`*`} element={<Navigate to={`${basePath}/summary`} replace />} />
            </Routes>
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
